import { deliveryText, paymentText, getTableName } from "../../../lang/orders";
import { formatNumber } from "../../../middleware/common-functions";
import { toDate } from "../../../middleware/dates";
import { OrderType } from "../../../models/order_model";
import { DashboardData } from "../../../models/sales_model";

const BOM = "\uFEFF";

const esc = (value: string | number | null | undefined): string => {
  if (value == null) return "";
  const str = String(value);
  if (
    str.includes(",") ||
    str.includes('"') ||
    str.includes("\n") ||
    str.includes("\r")
  ) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const row = (fields: (string | number | null | undefined)[]): string =>
  fields.map(esc).join(",");

export const downloadCsv = (csv: string, filename: string): void => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** Resumen / General tab */
export const generateResumenCsv = (dashboard: DashboardData): string => {
  const lines: string[] = [];

  lines.push(row(["Métrica", "Valor"]));
  lines.push(row(["Ingresos Totales", `$${formatNumber(dashboard.totalRevenue)}`]));
  lines.push(row(["Total de Ordenes", dashboard.totalOrders]));
  lines.push(row(["Ticket Promedio", `$${formatNumber(dashboard.avgTicket)}`]));
  lines.push(row(["Ventas POS", `$${formatNumber(dashboard.posSales.total)}`]));
  lines.push(row(["Ordenes POS", dashboard.posSales.count]));
  lines.push(row(["Ventas Online", `$${formatNumber(dashboard.onlineSales.total)}`]));
  lines.push(row(["Ordenes Online", dashboard.onlineSales.count]));
  lines.push(row(["Propinas", `$${formatNumber(dashboard.totalTips)}`]));
  lines.push(row(["Envíos", `$${formatNumber(dashboard.totalDeliveryFees)}`]));
  lines.push(row(["Canceladas", dashboard.cancelledOrders]));

  if (dashboard.byDeliveryType?.length) {
    lines.push("");
    lines.push(row(["Tipo de Entrega", "Ordenes", "Total"]));
    for (const d of dashboard.byDeliveryType) {
      lines.push(row([d.type, d.count, `$${formatNumber(d.total)}`]));
    }
  }

  if (dashboard.byPaymentType?.length) {
    lines.push("");
    lines.push(row(["Tipo de Pago", "Ordenes", "Total"]));
    for (const p of dashboard.byPaymentType) {
      lines.push(row([p.type, p.count, `$${formatNumber(p.total)}`]));
    }
  }

  if (dashboard.topProducts?.length) {
    lines.push("");
    lines.push(row(["Producto Top", "SKU", "Unidades", "Total"]));
    for (const p of dashboard.topProducts) {
      lines.push(row([p.title, p.model, p.quantity, `$${formatNumber(p.total)}`]));
    }
  }

  if (dashboard.salesByPerson?.length) {
    lines.push("");
    lines.push(row(["Persona", "Ordenes", "Total"]));
    for (const p of dashboard.salesByPerson) {
      lines.push(
        row([`${p.name} ${p.lastname}`.trim(), p.count, `$${formatNumber(p.total)}`])
      );
    }
  }

  if (dashboard.hourlyBreakdown?.length) {
    lines.push("");
    lines.push(row(["Hora", "Ordenes", "Total"]));
    for (const h of dashboard.hourlyBreakdown) {
      if (h.count === 0) continue;
      lines.push(row([`${h.hour}:00`, h.count, `$${formatNumber(h.total)}`]));
    }
  }

  if (dashboard.dailyBreakdown?.length) {
    lines.push("");
    lines.push(row(["Fecha", "Ordenes", "Total"]));
    for (const d of dashboard.dailyBreakdown) {
      lines.push(row([d.date, d.count, `$${formatNumber(d.total)}`]));
    }
  }

  return BOM + lines.join("\n") + "\n";
};

/** Ordenes tab */
export const generateOrdersCsv = (orders: OrderType[]): string => {
  const headers = [
    "#",
    "Fecha",
    "Hora",
    "Productos",
    "Persona",
    "Pago",
    "Mesa",
    "Tipo",
    "Envío",
    "Propina",
    "Total",
  ];

  const lines: string[] = [row(headers)];

  for (const item of orders) {
    const delivery = deliveryText(item.delivery_type, item.platform_name);
    const payment = paymentText(item.payment_type);
    const table_name = getTableName(item.table);
    const userName = item.user
      ? `${item.user.name} ${item.user.lastname || ""}`.trim()
      : "";

    lines.push(
      row([
        item.id,
        toDate(item.creation_date, {}, "date"),
        toDate(item.creation_date, {}, "hour"),
        item.resume_txt,
        userName,
        payment.txt,
        table_name,
        delivery.txt,
        item.delivery ? `$${formatNumber(item.delivery)}` : "",
        item.tip ? `$${formatNumber(item.tip)}` : "",
        `$${formatNumber(item.total)}`,
      ])
    );
  }

  return BOM + lines.join("\n") + "\n";
};

/** Productos tab */
export const generateProductsCsv = (
  orders: OrderType[],
  group: boolean
): string => {
  // Aggregate products same as products.tsx
  const aggregated: {
    id: string;
    unique: string;
    title: string;
    options: string;
    model: string;
    quantity: number;
    total: number;
  }[] = [];

  for (const order of orders) {
    for (const p of order.products) {
      if (!p.product) continue;
      const unique = group
        ? p.product.id
        : `${p.product.id}-${p.ingredients_opt}`;
      const found = aggregated.find((op) => op.unique === unique);
      if (found) {
        found.quantity += p.qty;
        found.total += p.price;
      } else {
        aggregated.push({
          id: p.product.id,
          unique,
          title: p.product.title,
          options: p.ingredients_opt ? `${p.ingredients_opt}` : "",
          model: p.product.model,
          quantity: p.qty,
          total: p.price,
        });
      }
    }
  }

  aggregated.sort((a, b) => b.total - a.total);

  const headers = group
    ? ["SKU", "Producto", "Unidades", "Precio Prom.", "Ventas"]
    : ["SKU", "Producto", "Variantes", "Unidades", "Precio Prom.", "Ventas"];

  const lines: string[] = [row(headers)];

  for (const item of aggregated) {
    const avgPrice = item.quantity > 0 ? item.total / item.quantity : 0;
    const fields: (string | number)[] = [
      item.model,
      item.title,
    ];
    if (!group) fields.push(item.options);
    fields.push(
      item.quantity,
      `$${formatNumber(avgPrice)}`,
      `$${formatNumber(item.total)}`
    );
    lines.push(row(fields));
  }

  return BOM + lines.join("\n") + "\n";
};

/** Por Persona tab */
export const generateByPersonCsv = (orders: OrderType[]): string => {
  const headers = [
    "#",
    "Fecha",
    "Hora",
    "Persona",
    "Productos",
    "Pago",
    "Mesa",
    "Tipo",
    "Total",
  ];

  const lines: string[] = [row(headers)];

  for (const item of orders) {
    const delivery = deliveryText(item.delivery_type, item.platform_name);
    const payment = paymentText(item.payment_type);
    const table_name = getTableName(item.table);
    const userName = item.user
      ? `${item.user.name} ${item.user.lastname || ""}`.trim()
      : "";

    lines.push(
      row([
        item.id,
        toDate(item.creation_date, {}, "date"),
        toDate(item.creation_date, {}, "hour"),
        userName,
        item.resume_txt,
        payment.txt,
        table_name,
        delivery.txt,
        `$${formatNumber(item.total)}`,
      ])
    );
  }

  return BOM + lines.join("\n") + "\n";
};
